/**
 * Shared Order field validation (PSA-260 sub-bugs 3 & 4).
 *
 * Accepted after spaces and hyphens are removed:
 * - Indian: KA33T234, MH12AB1234 (typed as MH-12-AB-1234)
 * - Bharat series: 22BH1234AA
 * - Australian: ABC123, AB12CD, 1ABC234 (2–8 letters/digits)
 */
const INDIAN_PLATE = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{1,4}$/;
const BH_PLATE = /^[0-9]{2}BH[0-9]{4}[A-Z]{1,2}$/;
const AU_PLATE = /^(?=.*[A-Z])[A-Z0-9]{2,8}$/;

/** Loose input mask so hyphenated plates are not blocked by the browser. */
export const VEHICLE_NUMBER_PATTERN = '^[A-Za-z0-9][A-Za-z0-9\\s-]{0,16}$';
export const VEHICLE_NUMBER_HELPER =
  'Enter a plate like KA33T234, MH-12-AB-1234, 22BH1234AA, or ABC123';

export function normalizeVehicleNumber(value) {
  if (value == null) return '';
  return String(value).trim().toUpperCase().replace(/[\s-]+/g, '');
}

export function isValidVehicleNumber(value, { required = false } = {}) {
  const normalized = normalizeVehicleNumber(value);
  if (!normalized) return !required;
  return INDIAN_PLATE.test(normalized) || BH_PLATE.test(normalized) || AU_PLATE.test(normalized);
}

export function vehicleNumbersMatch(left, right) {
  const a = normalizeVehicleNumber(left);
  const b = normalizeVehicleNumber(right);
  return Boolean(a) && a === b;
}

/** Compare calendar dates only (YYYY-MM-DD or Date / ISO string). */
export function toDateOnlyString(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match) return match[1];
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().split('T')[0];
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return value.toISOString().split('T')[0];
  }
  return null;
}

/**
 * Delivery Date must be on or after Order Creation / Order Date.
 * Empty delivery date is allowed.
 */
export function isDeliveryDateValid(deliveryDate, orderDate) {
  const delivery = toDateOnlyString(deliveryDate);
  if (!delivery) return true;
  const order = toDateOnlyString(orderDate);
  if (!order) return true;
  return delivery >= order;
}

export function validateOrderTransportAndDates({
  deliveryDate,
  orderDate,
  vehno,
  transmode,
  existingVehno,
} = {}) {
  const errors = {};

  if (!isDeliveryDateValid(deliveryDate, orderDate)) {
    errors.delivery_date =
      'Delivery Date cannot be earlier than the Order Creation Date.';
  }

  const roadRequired = String(transmode) === '1';
  const normalized = normalizeVehicleNumber(vehno);
  const unchanged = vehicleNumbersMatch(vehno, existingVehno);
  const valid = !normalized || isValidVehicleNumber(normalized);
  if (roadRequired && !normalized) {
    errors.vehno = 'Vehicle Number is required for Road transport.';
  } else if (normalized && !valid && !unchanged) {
    errors.vehno =
      'Vehicle Number must be like KA33T234, MH-12-AB-1234, 22BH1234AA, or an Australian plate.';
  }

  let normalizedVehno = normalized || null;
  if (unchanged && !valid) {
    normalizedVehno = existingVehno ?? normalizedVehno;
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    normalizedVehno,
  };
}
